<template>
  <v-flex xs12 id="paginationstart">
    <template v-if="normalizedItems.length">
      <GroupableItemListHeader
        slot="container_header"
        :manipulators="activeManipulators"
        :aggregations="aggregations"
        :actions="menuActions"
        :default-grouping="defaultGrouping"
        :default-sorting="defaultSorting"
        :default-filter="defaultFilter"
        @on-action="onAction"
        @change="onHeaderChanged"
        @expand-all="onExpandAll"
      />
      <template v-if="!isGrouped">
        <v-layout row wrap v-for="(item, id) in activePage" :key="id">
          <v-flex xs12 d-flex pa-1>
            <slot v-bind:item="item">{{ item }}</slot>
          </v-flex>
        </v-layout>
        <PaginationControl
          v-if="listConfigPaginationRequired"
          :page-sizes="listConfigPageSizes"
          :length="listConfigPageCount"
          @change="listConfigOnPaginationChange"
        />
      </template>
      <template v-else>
        <v-layout row wrap v-for="group in groupedItems" :key="group.id">
          <v-flex xs12 d-flex pl-3 pr-3 pb-2>
            <v-card class="overflow-hidden">
              <v-toolbar flat color="grey lighten-4">
                <v-toolbar-title :title="group.title" flat>
                  <slot name="group-header" :title="group.title" :id="group.id">
                    {{
                    group.title.length > 90 ? group.title.substring(0,90) + " ..." : group.title
                    }}
                  </slot>
                </v-toolbar-title>
                <v-spacer />
                <v-toolbar-items>
                  <v-btn color="primary" icon @click="toggleExpanded(group.id)">
                    <v-badge right color="secondary">
                      <template v-slot:badge>
                        <span>{{group.items.length}}</span>
                      </template>
                      <v-icon>
                        {{
                        expandedGroups[group.id] ? "expand_less" : "expand_more"
                        }}
                      </v-icon>
                    </v-badge>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
              <template v-if="expandedGroups[group.id]">
                <v-flex xs12 d-flex pa-1 row wrap v-for="(item, id) in group.items" :key="id">
                  <slot v-bind:item="item" class="transition-slow-in-fast-out">
                    {{
                    item
                    }}
                  </slot>
                </v-flex>
              </template>
            </v-card>
          </v-flex>
        </v-layout>
      </template>
    </template>
    <template v-else-if="actions.length">
      <v-flex class="text-xs-center" pb-2>
        <v-btn
          color="primary"
          @click="$emit(actions[0].event, actions[0].payload)"
        >{{actions[0].title}}</v-btn>
      </v-flex>
    </template>
  </v-flex>
</template>

<script>
import Vue from "vue";
import _ from "lodash";

import GroupableItemListHeader from "@/components/layouts/BaseLayouts/GroupableItemListHeader";
import PaginationControl from "@/components/controls/PaginationControl";
import ListConfigurationMixin from "@/components/mixins/ListConfigurationMixin";

import stable_sort from "@/utils/stable_sort";
import group_by from "@/utils/group_by";

function do_filter(ListFilter, FilterParams, List) {
  let auditItemList = List;
  for (const activ_filter of FilterParams) {
    const filter_cfg = ListFilter.find(v => v.id == activ_filter.id);
    if (filter_cfg) {
      const filter_helper = v => {
        return filter_cfg.callback(v, activ_filter.params);
      };
      auditItemList = auditItemList.filter(filter_helper);
    }
  }
  return auditItemList;
}

function do_sort(ListSorter, SorterParams, List) {
  let auditItemList = List;
  for (const activ_sort of SorterParams) {
    const sort_cfg = ListSorter.find(v => v.id == activ_sort.id);
    if (sort_cfg) {
      auditItemList = stable_sort(auditItemList, sort_cfg.cmp);
      if (activ_sort.desc) {
        auditItemList.reverse();
      }
    }
  }
  return auditItemList;
}

function do_group(ListManipulators, ManipulatorParams, List) {
  let auditItemList = List;
  const activ_group = ManipulatorParams.grouped_by;
  const group_cfg = ListManipulators.grouped_by.find(
    v => v.id == (activ_group ? activ_group.id : "")
  );
  const sort_cfg = ListManipulators.sort_by.find(
    v => v.id == (activ_group ? activ_group.id : "")
  );
  console.assert(!!activ_group == !!sort_cfg, "expect sort exist for grouping");
  if (activ_group && sort_cfg && group_cfg) {
    if (sort_cfg) {
      auditItemList = stable_sort(auditItemList, sort_cfg.cmp);
      if (activ_group.desc) {
        auditItemList.reverse();
      }
    }
    return group_by(auditItemList, group_cfg.group_infos);
  } else {
    return null;
  }
}

export default {
  name: "GroupableItemList",
  props: {
    listItems: {
      type: [Array, Object],
      required: true
    },
    manipulators: {
      type: Object,
      required: true
    },
    actions: {
      type: Array,
      default: () => []
    },
    listConfigKey: {
      type: String,
      required: true
    }
  },
  components: { GroupableItemListHeader, PaginationControl },
  mixins: [ListConfigurationMixin],
  data: () => {
    return {
      manipulatorParams: {
        filter_by: [],
        sort_by: [],
        grouped_by: null
      },
      expandedGroups: {}
    };
  },

  computed: {
    menuActions() {
      return this.actions.map(v => ({
        title: v.title,
        event: "on-action",
        payload: v
      }));
    },
    normalizedItems() {
      if (this.listItems instanceof Array) {
        return this.listItems;
      } else {
        return _.map(this.listItems, (val, idx) => ({ ...val, id: idx }));
      }
    },
    filteredItems() {
      return do_filter(
        this.manipulators.filter_by,
        this.manipulatorParams.filter_by,
        this.normalizedItems
      );
    },
    sortedItems() {
      return do_sort(
        this.manipulators.sort_by,
        this.manipulatorParams.sort_by,
        this.filteredItems
      );
    },
    groupedItems() {
      if (this.isGrouped) {
        return do_group(
          this.manipulators,
          this.manipulatorParams,
          this.sortedItems
        );
      } else {
        return null;
      }
    },
    isGrouped() {
      return this.manipulatorParams.grouped_by !== null;
    },
    pagedItems() {
      if (this.isGrouped) {
        return [];
      } else {
        return _.chunk(this.sortedItems, this.listConfigPageSize);
      }
    },
    activePage() {
      return this.pagedItems[this.listConfigPage - 1];
    },
    aggregations() {
      const aggregations = [];
      this.activeManipulators.filter_by.forEach(filter_by => {
        const bucketObj = {};
        this.normalizedItems.forEach(auditItem => {
          const aggregation = filter_by.aggregator(auditItem);

          if (aggregation) {
            for (const aggr of aggregation) {
              if (aggr.id in bucketObj) {
                bucketObj[aggr.id].resultCount += 1;
              } else {
                bucketObj[aggr.id] = {
                  ...aggr,
                  resultCount: 1
                };
              }
            }
          }
        });
        const buckets = _.map(bucketObj);
        if (buckets.length > 0) {
          buckets.sort((lhs, rhs) => {
            return rhs.resultCount - lhs.resultCount;
          });

          aggregations.push({
            id: filter_by.id,
            name: filter_by.name,
            buckets
          });
        }
      });

      return aggregations;
    },
    activeManipulators() {
      const extract = (availableItemList, Items) => {
        if (availableItemList instanceof Array) {
          return availableItemList
            .map(id => Items.find(item => item.id === id))
            .filter(v => v !== undefined);
        } else {
          return Items;
        }
      };
      if (
        this.listConfigManipulator &&
        (this.listConfigManipulator.availableFilter ||
          this.listConfigManipulator.availableSortings ||
          this.listConfigManipulator.availableGrouping ||
          this.listConfigManipulator.defaultSorting ||
          this.listConfigManipulator.defaultGrouping ||
          this.listConfigManipulator.defaultFilter)
      ) {
        const manipulators = this.manipulators;
        const activeManupulator = {
          filter_by: extract(
            this.listConfigManipulator.availableFilter,
            manipulators.filter_by
          ),
          sort_by: extract(
            this.listConfigManipulator.availableSortings,
            manipulators.sort_by
          ),
          grouped_by: extract(
            this.listConfigManipulator.availableGrouping,
            manipulators.grouped_by
          )
        };

        return activeManupulator;
      } else {
        return this.manipulators;
      }
    },
    defaultGrouping() {
      return this.listConfigManipulator.defaultGrouping;
    },
    defaultSorting() {
      return this.listConfigManipulator.defaultSorting;
    },
    defaultFilter() {
      return this.listConfigManipulator.defaultFilter || {};
    },

    // Overwrites from ListConfigurationMixin
    listConfigConfigKey() {
      return this.listConfigKey;
    },
    listConfigTotalItemCount() {
      return this.sortedItems.length;
    }
  },
  watch: {
    sortedItems: {
      immediate: true,
      handler(newVal) {
        this.$emit("change", {
          ListItems: newVal,
          ManipulatorParams: this.manipulatorParams
        });
      }
    }
  },

  methods: {
    toggleExpanded(GroupId) {
      if (this.expandedGroups[GroupId]) {
        this.expandedGroups[GroupId] = false;
      } else {
        Vue.set(this.expandedGroups, GroupId, true);
      }
    },
    onHeaderChanged(data) {
      this.manipulatorParams = data;
    },
    onExpandAll(expand) {
      if (expand) {
        const allGroups = {};
        this.groupedItems.forEach(group => {
          allGroups[group.id] = true;
        });
        this.expandedGroups = allGroups;
      } else {
        this.expandedGroups = {};
      }
    },
    onAction(action) {
      this.$emit(action.event, action.payload);
    }
  }
};
</script>
